import { useEffect, useMemo, useRef, useState } from 'react';
import Point from '@arcgis/core/geometry/Point';
import * as locator from '@arcgis/core/rest/locator';
import { Check, Pencil, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TwoStepNoticeFields } from 'components/MapPage/CorporateViewTabs/NotificationsTab/TwoStepNoticeFields';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import { hwcgRosterPositionNames } from 'components/IncidentConfig/CreateIncident/hwcgRosterTemplates';
import { WGS84_SPATIAL_REFERENCE } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/spatialReference';
import { useRosterTemplatesCatalog } from 'hooks/incident/useRoster';
import {
  useAddHwcgContact,
  useUpdateHwcgContact,
  type HwcgContactList,
  type HwcgContactListMember,
  type HwcgPocContact,
} from 'hooks/hwcgContactLists/hwcgContactListHooks';
import { toast } from 'react-toastify';

import type { HwcgContactListId, HwcgGroupId } from './hwcgGroupActions';
import type { HwcgIncidentSpecificsState } from './hwcgIncidentSpecifics';
import {
  HWCG_EOC_CHANNELS,
  hwcgActivateScriptFor,
  stripHwcgGroupActionsLinkToken,
  stripHwcgGroupReplyPrompts,
  hwcgEocCompanyMatches,
  hwcgEocNotifyScript,
  type HwcgEocChannel,
  type HwcgEocNoticeState,
  type HwcgEocPoint,
} from './hwcgEocNotice';
import { HwcgGroupNotifyComposeModal } from './HwcgGroupNotifyComposeModal';
import {
  hwcgLiveNotifyUsesComposer,
  hwcgNotifyComposerContacts,
} from './hwcgGroupNotifyComposer';
import type { HwcgContactNoticeStatus } from './hwcgNoticeContactStatus';
import {
  deliveryStateLabel,
  formatNoticeSentAt,
  hwcgEocNoticeResponse,
} from './hwcgNoticeContactStatus';

const GEOCODE_URL =
  'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer';

export const CHANNEL_LABELS: Record<HwcgEocChannel, string> = {
  inApp: 'PRATUS',
  email: 'Email',
  sms: 'SMS',
  voice: 'Call',
};

function pocLabel(value: HwcgPocContact | null): string {
  if (value === 'primary') return 'Primary';
  if (value === 'secondary') return 'Secondary';
  return '—';
}

type RecipientContact = HwcgContactListMember & {
  constituency: HwcgContactListId;
};

type RecipientDraft = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  primaryCategory: string;
  secondaryCategory: string;
  pocContact: HwcgPocContact | null;
  serviceProvider: string;
  userGuid: string | null;
  constituency: HwcgContactListId;
  authorizedIndividual: boolean;
};

const EMPTY_RECIPIENT: RecipientDraft = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  primaryCategory: '',
  secondaryCategory: '',
  pocContact: null,
  serviceProvider: '',
  userGuid: null,
  constituency: 'eoc',
  authorizedIndividual: false,
};

function draftFromContact(contact: RecipientContact): RecipientDraft {
  return {
    firstName: contact.firstName ?? '',
    lastName: contact.lastName ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    primaryCategory: contact.primaryCategory ?? '',
    secondaryCategory: contact.secondaryCategory ?? '',
    pocContact: contact.pocContact,
    serviceProvider: contact.serviceProvider ?? '',
    userGuid: contact.userGuid,
    constituency: contact.constituency,
    authorizedIndividual: contact.authorizedIndividual === true,
  };
}

function CategorySelect({
  label,
  value,
  positions,
  onChange,
}: {
  label: string;
  value: string;
  positions: string[];
  onChange: (next: string) => void;
}) {
  const trimmed = value.trim();
  const names =
    trimmed && !positions.includes(trimmed)
      ? [trimmed, ...positions]
      : positions;
  const options = names.map((name) => ({ value: name, label: name }));
  return (
    <SearchableSelect
      aria-label={label}
      options={options}
      value={options.find((option) => option.value === trimmed) ?? null}
      clearable
      placeholder="Select position"
      contentClassName="tw:min-w-72"
      onChange={(next) => onChange(next?.value ?? '')}
    />
  );
}

function RecipientFields({
  draft,
  positions,
  pending,
  saveLabel,
  cancelLabel,
  trailingEmptyCells = 0,
  onChange,
  onSave,
  onCancel,
}: {
  draft: RecipientDraft;
  positions: string[];
  pending: boolean;
  saveLabel: string;
  cancelLabel: string;
  /** Empty cells before Actions when status columns are shown (sendMode). */
  trailingEmptyCells?: number;
  onChange: (next: RecipientDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const patch = (partial: Partial<RecipientDraft>) =>
    onChange({ ...draft, ...partial });
  return (
    <>
      <TableCell>
        <div className="tw:flex tw:min-w-40 tw:flex-col tw:gap-1">
          <Input
            aria-label="First name"
            placeholder="First name"
            value={draft.firstName}
            onChange={(event) => patch({ firstName: event.target.value })}
          />
          <Input
            aria-label="Last name"
            placeholder="Last name"
            value={draft.lastName}
            onChange={(event) => patch({ lastName: event.target.value })}
          />
        </div>
      </TableCell>
      <TableCell>
        <Select
          value={draft.pocContact ?? 'none'}
          onValueChange={(value) =>
            patch({
              pocContact: value === 'none' ? null : (value as HwcgPocContact),
            })
          }
        >
          <SelectTrigger aria-label="EOC contact" className="tw:w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">—</SelectItem>
            <SelectItem value="primary">Primary</SelectItem>
            <SelectItem value="secondary">Secondary</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <CategorySelect
          label="Primary category"
          positions={positions}
          value={draft.primaryCategory}
          onChange={(primaryCategory) => patch({ primaryCategory })}
        />
      </TableCell>
      <TableCell>
        <CategorySelect
          label="Secondary category"
          positions={positions}
          value={draft.secondaryCategory}
          onChange={(secondaryCategory) => patch({ secondaryCategory })}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label="Email"
          value={draft.email}
          onChange={(event) => patch({ email: event.target.value })}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label="Phone"
          value={draft.phone}
          onChange={(event) => patch({ phone: event.target.value })}
        />
      </TableCell>
      {Array.from({ length: trailingEmptyCells }, (_, index) => (
        <TableCell key={`pad-${index}`} />
      ))}
      <TableCell>
        <div className="tw:flex tw:justify-end tw:gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={saveLabel}
            disabled={pending}
            onClick={onSave}
          >
            <Check className="tw:size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={cancelLabel}
            onClick={onCancel}
          >
            <X className="tw:size-4" />
          </Button>
        </div>
      </TableCell>
    </>
  );
}

export function EocLocationField({
  companyName,
  label,
  mode,
  onCommit,
}: {
  companyName: string;
  label: string;
  mode: 'eoc' | 'report-to';
  onCommit: (next: {
    locationLabel: string;
    location: HwcgEocPoint | null;
  }) => void;
}) {
  const [addresses, setAddresses] = useState<
    { text: string; magicKey: string }[]
  >([]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const trimmed = label.trim();

  useEffect(() => {
    if (trimmed.length < 3) return;
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      void locator
        .suggestLocations(GEOCODE_URL, {
          text: trimmed,
          categories: ['Address', 'Postal', 'Populated Place'],
          location: new Point({
            longitude: -98.5,
            latitude: 39.8,
            spatialReference: WGS84_SPATIAL_REFERENCE,
          }),
        })
        .then((results) => {
          if (cancelled) return;
          setAddresses(
            results
              .flatMap((result) => {
                if (!result.text || !result.magicKey) return [];
                return [{ text: result.text, magicKey: result.magicKey }];
              })
              .slice(0, 5)
          );
        })
        .catch(() => {
          if (!cancelled) setAddresses([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [trimmed]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const choose = async (suggestion: { text: string; magicKey: string }) => {
    try {
      const matches = await locator.addressToLocations(GEOCODE_URL, {
        address: { SingleLine: suggestion.text },
        magicKey: suggestion.magicKey,
        maxLocations: 1,
      });
      const location = matches[0]?.location;
      if (location?.latitude == null || location.longitude == null) return;
      onCommit({
        locationLabel: suggestion.text,
        location: {
          type: 'Point',
          coordinates: [location.longitude, location.latitude],
        },
      });
      setOpen(false);
    } catch {
      setOpen(false);
    }
  };

  return (
    <div className="tw:grid tw:gap-1.5">
      <div className="tw:flex tw:items-center tw:gap-2">
        <div className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
          {mode === 'report-to' ? 'Report-to location' : 'EOC location'}{' '}
          <span className="tw:text-destructive">*</span>
        </div>
        {mode === 'report-to' ? (
          <span className="tw:text-[10px] tw:font-medium tw:text-orange-500">
            Activation script
          </span>
        ) : null}
      </div>
      {mode === 'eoc' && companyName.trim() ? (
        <p className="tw:text-xs tw:text-emerald-600">
          From {companyName.trim()}
        </p>
      ) : null}
      {mode === 'report-to' ? (
        <p className="tw:text-xs tw:text-muted-foreground">
          Defaults to the Responsible Party office location when unset.
        </p>
      ) : null}
      <div ref={rootRef} className="tw:relative">
        <Input
          aria-label={
            mode === 'report-to' ? 'Report-to location' : 'EOC location'
          }
          value={label}
          placeholder={
            mode === 'report-to'
              ? 'Select report-to location...'
              : 'Type an address or place'
          }
          onChange={(event) => {
            onCommit({ locationLabel: event.target.value, location: null });
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {open && addresses.length > 0 ? (
          <div className="tw:absolute tw:z-20 tw:mt-1 tw:max-h-56 tw:w-full tw:overflow-y-auto tw:rounded-lg tw:border tw:border-border tw:bg-popover tw:p-1 tw:text-sm tw:shadow-md">
            {addresses.map((suggestion) => (
              <button
                key={suggestion.magicKey}
                type="button"
                className="tw:block tw:w-full tw:rounded-md tw:px-2 tw:py-1 tw:text-left tw:hover:bg-muted"
                onClick={() => void choose(suggestion)}
              >
                {suggestion.text}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function EocOutcomeChoices({
  outcome,
  note,
  pending,
  onRecord,
}: {
  outcome: 'activated' | 'cannot' | null;
  note: string | null;
  pending: boolean;
  onRecord: (outcome: 'activated' | 'cannot', note: string) => void;
}) {
  const [choice, setChoice] = useState<'activated' | 'cannot' | ''>(
    outcome ?? ''
  );
  const [draft, setDraft] = useState(note ?? '');

  useEffect(() => {
    setChoice(outcome ?? '');
    setDraft(note ?? '');
  }, [note, outcome]);

  const cannot = choice === 'cannot';
  const canSave =
    !pending && (choice === 'activated' || (cannot && draft.trim().length > 0));

  return (
    <div className="tw:space-y-3 tw:rounded-md tw:border tw:border-border tw:px-3 tw:py-3">
      <RadioGroup
        aria-label="EOC outcome"
        value={choice}
        disabled={pending}
        onValueChange={(next) => {
          if (next === 'activated' || next === 'cannot') setChoice(next);
        }}
        className="tw:gap-2"
      >
        <div className="tw:flex tw:items-center tw:gap-2">
          <RadioGroupItem value="activated" id="eoc-outcome-activated" />
          <Label
            htmlFor="eoc-outcome-activated"
            className="tw:text-sm tw:font-normal"
          >
            EOC activated
          </Label>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          <RadioGroupItem value="cannot" id="eoc-outcome-cannot" />
          <Label
            htmlFor="eoc-outcome-cannot"
            className="tw:text-sm tw:font-normal"
          >
            EOC cannot be activated
          </Label>
        </div>
      </RadioGroup>
      {cannot ? (
        <Textarea
          aria-label="Why the EOC cannot be activated"
          value={draft}
          disabled={pending}
          maxLength={2000}
          placeholder="Explain why the EOC cannot be activated"
          onChange={(event) => setDraft(event.target.value)}
        />
      ) : null}
      <Button
        type="button"
        size="sm"
        disabled={!canSave}
        onClick={() => {
          if (choice === 'activated' || choice === 'cannot')
            onRecord(choice, draft.trim());
        }}
      >
        Save
      </Button>
    </div>
  );
}

export function HwcgEocActionPanel({
  groupId,
  groupLabel,
  action,
  companyName,
  specifics,
  notice,
  contacts,
  matchCompany,
  addTarget,
  onChange,
  sendMode = false,
  incidentId,
  onSend,
  sendPending = false,
  contactStatusById = {},
}: {
  groupId: HwcgGroupId;
  groupLabel: string;
  action: 'activate' | 'notify';
  companyName: string;
  specifics: HwcgIncidentSpecificsState;
  notice: HwcgEocNoticeState;
  contacts: RecipientContact[];
  matchCompany: boolean;
  addTarget: { constituency: HwcgContactListId; memberCompany: string };
  onChange: (next: HwcgEocNoticeState) => void;
  /** Group Actions tab: per-row / multi-select send + delivery columns. */
  sendMode?: boolean;
  /** Locks Associated Incident to the incident this panel is open on. */
  incidentId?: string;
  onSend?: (
    contactIds: string[],
    options?: { noticeScript: string; channels: HwcgEocChannel[] }
  ) => void;
  sendPending?: boolean;
  contactStatusById?: Record<string, HwcgContactNoticeStatus>;
}) {
  const sendStatusColumns = sendMode ? 3 : 0;
  const notifyComposer = hwcgLiveNotifyUsesComposer(sendMode, action);
  const addContact = useAddHwcgContact();
  const [composerOpen, setComposerOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const updateContact = useUpdateHwcgContact(editingId ?? '');
  const [draft, setDraft] = useState<RecipientDraft>(EMPTY_RECIPIENT);
  const catalogQuery = useRosterTemplatesCatalog();
  const positions = useMemo(
    () => hwcgRosterPositionNames(catalogQuery.data?.templates ?? []),
    [catalogQuery.data]
  );
  const seenRef = useRef(new Set<string>());
  const companyRef = useRef(companyName.trim().toLowerCase());

  const matches = useMemo(
    () =>
      matchCompany
        ? contacts.filter((contact) =>
            hwcgEocCompanyMatches(contact.memberCompany, companyName)
          )
        : contacts,
    [contacts, companyName, matchCompany]
  );
  const matchKey = matches.map((contact) => contact.id).join('|');
  const selectedKey = notice.recipientContactIds.join('|');

  useEffect(() => {
    if (notifyComposer) return;
    const companyKey = companyName.trim().toLowerCase();
    if (companyRef.current !== companyKey) {
      companyRef.current = companyKey;
      seenRef.current = new Set();
    }
    const matchIds = matchKey ? matchKey.split('|') : [];
    const selected = new Set(selectedKey ? selectedKey.split('|') : []);
    const next = matchIds.filter(
      (id) => selected.has(id) || !seenRef.current.has(id)
    );
    for (const id of matchIds) seenRef.current.add(id);
    const same =
      next.length === (selectedKey ? selectedKey.split('|').length : 0) &&
      next.every((id, index) => id === (selectedKey.split('|')[index] ?? ''));
    if (!same) onChange({ ...notice, recipientContactIds: next });
  }, [companyName, matchKey, selectedKey, notice, onChange, notifyComposer]);

  const activateGroup =
    groupId === 'responsible_party' ? 'responsible_party' : 'eoc';
  const activateTemplate = hwcgActivateScriptFor(
    activateGroup,
    { ...notice, activateScript: null },
    specifics
  );
  const notifyScript = stripHwcgGroupReplyPrompts(
    notice.notifyScript ?? hwcgEocNotifyScript(specifics)
  );
  const noticeScript =
    action === 'activate'
      ? stripHwcgGroupReplyPrompts(
          stripHwcgGroupActionsLinkToken(
            notice.activateScript ?? activateTemplate
          )
        )
      : notifyScript;
  const selected = new Set(notice.recipientContactIds);
  const title = action === 'activate' ? 'Activation Notice' : 'Notification';
  const locationMode =
    action === 'activate'
      ? groupId === 'responsible_party'
        ? 'report-to'
        : 'eoc'
      : 'none';
  const showReportToTime = locationMode === 'report-to' || groupId === 'eoc';
  const addDisabled = matchCompany && !companyName.trim();

  const patch = (partial: Partial<HwcgEocNoticeState>) =>
    onChange({ ...notice, ...partial });

  const toggleRecipient = (id: string, checked: boolean) => {
    const next = checked
      ? [...notice.recipientContactIds, id]
      : notice.recipientContactIds.filter((current) => current !== id);
    patch({ recipientContactIds: next });
  };

  const toggleChannel = (channel: HwcgEocChannel, checked: boolean) => {
    const next = checked
      ? [...notice.channels, channel]
      : notice.channels.filter((current) => current !== channel);
    patch({ channels: next });
  };

  const payloadFromDraft = (memberCompany: string) => ({
    constituency: draft.constituency,
    memberCompany,
    serviceProvider: draft.serviceProvider,
    firstName: draft.firstName,
    lastName: draft.lastName,
    email: draft.email,
    phone: draft.phone,
    primaryCategory: draft.primaryCategory,
    secondaryCategory: draft.secondaryCategory,
    pocContact: draft.pocContact,
    userGuid: draft.userGuid,
    authorizedIndividual: draft.authorizedIndividual,
  });

  const knownMemberIds = () => new Set(contacts.map((contact) => contact.id));

  const findAddedContactId = (
    lists: HwcgContactList[],
    before: Set<string>
  ): string | null => {
    for (const list of lists) {
      for (const member of list.members) {
        if (!before.has(member.id)) return member.id;
      }
    }
    return null;
  };

  const saveDraft = () => {
    if (matchCompany && !companyName.trim()) return;
    const before = knownMemberIds();
    addContact.mutate(
      {
        ...payloadFromDraft(addTarget.memberCompany),
        constituency: addTarget.constituency,
      },
      {
        onSuccess: ({ lists }) => {
          const addedId = findAddedContactId(lists, before);
          setAdding(false);
          setDraft(EMPTY_RECIPIENT);
          if (addedId) {
            seenRef.current.add(addedId);
            if (!notice.recipientContactIds.includes(addedId)) {
              patch({
                recipientContactIds: [...notice.recipientContactIds, addedId],
              });
            }
          }
          toast.success('Recipient added to the organization contact list.');
        },
      }
    );
  };

  const saveEdit = () => {
    if (!editingId) return;
    const current = matches.find((contact) => contact.id === editingId);
    updateContact.mutate(
      payloadFromDraft(current?.memberCompany?.trim() || companyName.trim()),
      {
        onSuccess: () => {
          setEditingId(null);
          setDraft(EMPTY_RECIPIENT);
        },
      }
    );
  };

  return (
    <div className="tw:space-y-4">
      <div>
        <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
          <h4 className="tw:text-sm tw:font-semibold">{title}</h4>
          <span className="tw:text-[11px] tw:uppercase tw:tracking-wide tw:text-muted-foreground">
            Auto-filled from incident specifics
          </span>
        </div>
        <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
          {sendMode
            ? action === 'activate'
              ? 'Select recipients and send the activation notice. Status updates as people reply.'
              : 'Create a notification from this group contact list. The log lists every notification sent from this tab.'
            : action === 'activate'
              ? 'Preview the activation notice for this group. The activation request will be sent to the recipients when you confirm creation of the incident.'
              : 'Preview the notification. The notification will be sent when you confirm creation of the incident.'}
        </p>
        <div className="tw:mt-3 tw:space-y-1">
          <div className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
            {action === 'activate'
              ? 'Sending to (marked activate)'
              : 'Sending to (marked notify)'}
          </div>
          <Badge variant="secondary">{groupLabel}</Badge>
        </div>
      </div>

      {locationMode === 'none' ? null : (
        <div
          className={
            showReportToTime ? 'tw:grid tw:gap-3 tw:sm:grid-cols-2' : undefined
          }
        >
          <EocLocationField
            companyName={companyName}
            label={notice.locationLabel}
            mode={locationMode}
            onCommit={(next) => patch(next)}
          />
          {showReportToTime ? (
            <div className="tw:grid tw:gap-1.5">
              <div className="tw:flex tw:items-center tw:gap-2">
                <div className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                  Report-to time <span className="tw:text-destructive">*</span>
                </div>
                <span className="tw:text-[10px] tw:font-medium tw:text-orange-500">
                  Activation script
                </span>
              </div>
              <Input
                aria-label="Report-to time"
                placeholder="--:-- --"
                value={notice.reportToTime}
                onChange={(event) =>
                  patch({ reportToTime: event.target.value })
                }
              />
            </div>
          ) : null}
        </div>
      )}

      {notifyComposer ? (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
          <div>
            <div className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
              Notification
            </div>
            <p className="tw:mt-1 tw:mb-0 tw:text-xs tw:text-muted-foreground">
              The script starts from this group&apos;s notification template.
              Recipients come from its contact list section.
            </p>
          </div>
          <Button type="button" size="sm" onClick={() => setComposerOpen(true)}>
            + Create Notification
          </Button>
          <HwcgGroupNotifyComposeModal
            open={composerOpen}
            onOpenChange={setComposerOpen}
            groupLabel={groupLabel}
            notice={notice}
            specifics={specifics}
            contacts={hwcgNotifyComposerContacts(
              groupId,
              contacts,
              companyName
            )}
            incidentId={incidentId}
            pending={sendPending}
            onSend={(input) => {
              const cleaned = stripHwcgGroupReplyPrompts(input.noticeScript);
              patch({
                notifyScript:
                  cleaned === hwcgEocNotifyScript(specifics) ? null : cleaned,
                channels: input.channels,
                recipientContactIds: input.contactIds,
              });
              onSend?.(input.contactIds, {
                noticeScript: cleaned,
                channels: input.channels,
              });
              setComposerOpen(false);
            }}
          />
        </div>
      ) : (
        <>
          <div className="tw:space-y-2">
            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
              <div className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                Recipients (contact list
                {companyName.trim() ? ` — ${companyName.trim()}` : ''}){' '}
                <span className="tw:text-destructive">*</span>
              </div>
              <div className="tw:flex tw:flex-wrap tw:gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={addDisabled}
                  onClick={() => {
                    setEditingId(null);
                    setDraft(EMPTY_RECIPIENT);
                    setAdding((current) => !current);
                  }}
                >
                  + Add
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    patch({
                      recipientContactIds: matches.map((contact) => contact.id),
                    })
                  }
                >
                  Select all
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => patch({ recipientContactIds: [] })}
                >
                  Clear
                </Button>
                {sendMode ? (
                  <Button
                    type="button"
                    size="sm"
                    disabled={
                      sendPending ||
                      notice.recipientContactIds.length === 0 ||
                      !onSend
                    }
                    onClick={() => onSend?.(notice.recipientContactIds)}
                  >
                    Send selected
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="tw:overflow-x-auto tw:rounded-md tw:border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="tw:w-10">
                      <span className="tw:sr-only">Include</span>
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>EOC contact</TableHead>
                    <TableHead>Primary category</TableHead>
                    <TableHead>Secondary category</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    {sendMode ? (
                      <>
                        <TableHead>Sent</TableHead>
                        <TableHead>Delivery</TableHead>
                        <TableHead>
                          {action === 'notify' ? 'Acknowledged' : 'Activated'}
                        </TableHead>
                      </>
                    ) : null}
                    <TableHead className="tw:w-24">
                      <span className="tw:sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adding ? (
                    <TableRow>
                      <TableCell />
                      <RecipientFields
                        draft={draft}
                        positions={positions}
                        pending={addContact.isPending}
                        saveLabel="Save recipient"
                        cancelLabel="Cancel add"
                        trailingEmptyCells={sendStatusColumns}
                        onChange={setDraft}
                        onSave={saveDraft}
                        onCancel={() => {
                          setAdding(false);
                          setDraft(EMPTY_RECIPIENT);
                        }}
                      />
                    </TableRow>
                  ) : null}
                  {matches.length === 0 && !adding ? (
                    <TableRow>
                      <TableCell
                        colSpan={8 + sendStatusColumns}
                        className="tw:text-muted-foreground"
                      >
                        {matchCompany && !companyName.trim()
                          ? 'Select a responsible party to list recipients.'
                          : 'Nobody on this list matches this group.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    matches.map((contact) =>
                      editingId === contact.id ? (
                        <TableRow key={contact.id}>
                          <TableCell>
                            <Checkbox
                              aria-label={`Include ${contact.name}`}
                              checked={selected.has(contact.id)}
                              onCheckedChange={(value) =>
                                toggleRecipient(contact.id, value === true)
                              }
                            />
                          </TableCell>
                          <RecipientFields
                            draft={draft}
                            positions={positions}
                            pending={updateContact.isPending}
                            saveLabel="Save recipient"
                            cancelLabel="Cancel edit"
                            trailingEmptyCells={sendStatusColumns}
                            onChange={setDraft}
                            onSave={saveEdit}
                            onCancel={() => {
                              setEditingId(null);
                              setDraft(EMPTY_RECIPIENT);
                            }}
                          />
                        </TableRow>
                      ) : (
                        <TableRow key={contact.id}>
                          <TableCell>
                            <Checkbox
                              aria-label={`Include ${contact.name}`}
                              checked={selected.has(contact.id)}
                              onCheckedChange={(value) =>
                                toggleRecipient(contact.id, value === true)
                              }
                            />
                          </TableCell>
                          <TableCell>{contact.name}</TableCell>
                          <TableCell>{pocLabel(contact.pocContact)}</TableCell>
                          <TableCell>
                            {contact.primaryCategory?.trim() || '—'}
                          </TableCell>
                          <TableCell>
                            {contact.secondaryCategory?.trim() || '—'}
                          </TableCell>
                          <TableCell>{contact.email?.trim() || '—'}</TableCell>
                          <TableCell>{contact.phone?.trim() || '—'}</TableCell>
                          {sendMode ? (
                            <>
                              <TableCell>
                                {formatNoticeSentAt(
                                  contactStatusById[contact.id]?.sentAt ?? null
                                )}
                              </TableCell>
                              <TableCell>
                                {deliveryStateLabel(
                                  contactStatusById[contact.id]?.deliveryState
                                )}
                              </TableCell>
                              <TableCell>
                                {hwcgEocNoticeResponse(
                                  contactStatusById[contact.id]
                                )}
                              </TableCell>
                            </>
                          ) : null}
                          <TableCell>
                            <div className="tw:flex tw:items-center tw:gap-1">
                              {sendMode ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={sendPending || !onSend}
                                  onClick={() => onSend?.([contact.id])}
                                >
                                  Send
                                </Button>
                              ) : null}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-label={`Edit ${contact.name}`}
                                onClick={() => {
                                  setAdding(false);
                                  setEditingId(contact.id);
                                  setDraft(draftFromContact(contact));
                                }}
                              >
                                <Pencil className="tw:size-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    )
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <TwoStepNoticeFields
            stepTwo={{
              label:
                action === 'activate' ? 'Activation notice' : 'Notification',
              title:
                action === 'activate'
                  ? 'Activation script'
                  : 'Notification script',
              hint: 'Sent once. The login agreement is part of this script.',
            }}
            stepTwoValue={noticeScript}
            onStepTwo={(next) => {
              if (action === 'activate') {
                const cleaned = stripHwcgGroupReplyPrompts(
                  stripHwcgGroupActionsLinkToken(next)
                );
                patch({
                  activateScript: cleaned === activateTemplate ? null : cleaned,
                });
                return;
              }
              const cleaned = stripHwcgGroupReplyPrompts(next);
              patch({
                notifyScript:
                  cleaned === hwcgEocNotifyScript(specifics) ? null : cleaned,
              });
            }}
          />
        </>
      )}

      <div className="tw:grid tw:gap-1.5">
        <div className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
          Communication method <span className="tw:text-destructive">*</span>
        </div>
        <div className="tw:flex tw:flex-wrap tw:gap-3">
          {HWCG_EOC_CHANNELS.map((channel) => (
            <label
              key={channel}
              className="tw:flex tw:items-center tw:gap-2 tw:text-sm"
            >
              <Checkbox
                checked={notice.channels.includes(channel)}
                onCheckedChange={(value) =>
                  toggleChannel(channel, value === true)
                }
              />
              {CHANNEL_LABELS[channel]}
            </label>
          ))}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                render={
                  <span className="tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground">
                    <Checkbox checked={false} disabled />
                    Microsoft Teams
                  </span>
                }
              />
              <TooltipContent>Upgrade to Enable</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
}
